import React, { FC, ReactElement, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Rating from '@mui/material/Rating';
import { NavigateFunction } from 'react-router';
import { Theme } from '@mui/material';
import { clsx } from 'clsx';
// Components
import Text from '../../../components/text';
import Space from '../../../components/layout/space';
import InputField from '../../../components/form/input';
import RadiusButton from '../../../components/buttons/radiusButton';
import EditReservationWrapper from '../reservations/editReservationWrapper';
import Loader from '../../../components/loader';
import Separator from '../../../components/layout/separator';
import SvgIcon from '../../../components/icons/SvgIcon';
// Hooks
import { useGetPolicies } from '../../../core/services/policies';
import { useGetTags, useUpdateRide } from '../../../core/services/rides';
// Resources
import { stringsRes, rps } from '../../../resources/strings';
import { EditRideInput, ExtraType, GetPoliciesOutput, GetTagsOuput, Ride, StopType } from '../../../core/apiTypes';
import { PropsFromModal } from '../../../components/modals/createModalHOC';
import { findStop, getStopFullname, isBookingFinishedNotCanceled } from '../../../utils/business';
import useGlobalStyles from '../../../components/hook/useGlobalStyles';
import {
    DEFAULT_TIPS_ARRAY,
    MAX_RATING_TAGS,
    MAX_TIP_AMOUNT,
    MIN_NO_COMMENT,
    MIN_RATING_FOR_TIPS,
} from '../../../utils/constants';
import { formatDateFromTimestamp } from '../../../utils/date';
import { ToastWithIcon } from '../toastProvider';
import { capitalizeFirstLetter } from '../../../utils/strings';
import { IconAxel, IconCommon } from '../../../resources/images';

interface EditHistoryTipsAndRatingProps extends PropsFromModal {
    ride: Ride;
    navigate: NavigateFunction;
    stars?: number;
}

const EditHistoryTipsAndRating: FC = (props: EditHistoryTipsAndRatingProps): ReactElement => {
    const { ride, stars, onRequestClose } = props;

    const classes = useStyles();
    const globalClasses = useGlobalStyles();

    //= ======================================//
    //= ============== Queries ===============//
    //= ======================================//

    const updateRide = useUpdateRide(true);

    const { data: tags } = useGetTags<GetTagsOuput>();

    const { data: groupPolicyDatas, isFetching } = useGetPolicies<GetPoliciesOutput>({
        enabled: isBookingFinishedNotCanceled(ride.status) && !!ride.group?.uuid,
        variables: { groups: [ride.group?.uuid] },
    });
    const groupPolicy = groupPolicyDatas?.group?.[0];

    const tips = ride?.price_details?.extras?.find((e) => e.type === ExtraType.TIPS);
    const canAddTip =
        !groupPolicy?.tip_not_allowed &&
        (!ride?.billing || (ride?.billing?.status !== 'issued' && ride?.billing?.status !== 'ready'));

    const [message, setMessage] = useState<string>(ride.driver_rating?.comment ?? '');
    const [rating, setRating] = React.useState<number | null>(ride.driver_rating?.score ?? stars ?? null);
    const [tip, setTip] = React.useState<number | null>(null);
    const [customTip, setCustomTip] = React.useState<number | null>(tips?.amount ?? null);
    const [showComment, setShowComment] = React.useState<boolean>(false);
    const [showTips, setShowTips] = React.useState<boolean>(ride.driver_rating?.score >= MIN_RATING_FOR_TIPS);
    const [selectedTags, setSelectedTags] = React.useState<string[]>(ride.driver_rating?.tags ?? []);

    //= ========================================//
    //= ============== useEffect ===============//
    //= ========================================//

    React.useEffect(() => {
        if (
            (ride.driver_rating?.score !== undefined && ride.driver_rating.score < MIN_NO_COMMENT) ||
            (stars !== undefined && stars < MIN_NO_COMMENT)
        ) {
            setShowComment(true);
        } else {
            setShowComment(false);
        }
    }, [ride.driver_rating?.score, stars]);

    //= ======================================//
    //= ============== Actions ===============//
    //= ======================================//

    const onChangeRating = (event, newValue) => {
        const hasComment = newValue < MIN_NO_COMMENT;
        setShowComment(hasComment);
        setRating(newValue);
    };

    const onChangeTag = (tag: string) => (): void => {
        setSelectedTags((prev) => {
            // Check if the tag is already in the array
            if (prev.includes(tag)) {
                return prev.filter((t) => t !== tag);
            }
            return [...prev, tag];
        });
    };

    const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setMessage(event.target.value);
    };

    const onChangeTip = (newCustomTip: number) => (): void => {
        setTip(tip === newCustomTip ? null : newCustomTip);
        setCustomTip(null);
    };

    const onChangeCustomTip = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const val = +event.target.value;
        if (!Number.isNaN(val)) {
            // Limit the tip to the max amount tip ride price or MAX_TIP_AMOUNT
            const newTip = Math.min(Math.max(val, 0), Math.min(ride.price_details.client_final_price, MAX_TIP_AMOUNT));

            setTip(null);
            setCustomTip(Math.abs(newTip));
        }
    };

    // const onGoToSupport = (): void => {
    //     navigateTo(navigate, `${paths.support}?ride_reference=${ride.id}`);
    //     onRequestClose();
    // };

    const onConfirm = (): void => {
        if (canAddTip && !showTips && rating >= MIN_RATING_FOR_TIPS) {
            setShowTips(true);
        } else {
            const finalTip = customTip ?? tip;

            const payload: EditRideInput = {
                id: ride.id,
                ...(finalTip && { tip_amount: finalTip }),
                ...(selectedTags?.length && { driver_rating_tags: selectedTags }),
                driver_rating_score: rating,
                ...(message && rating < 5 && { driver_rating_comment: message }),
            };

            // console.log(payload);

            updateRide.mutate(payload, {
                onSuccess: () => {
                    onRequestClose?.();

                    let texts = stringsRes.tripHistory.tipsAndRating.ratingThanks;
                    let icon = null;
                    let { title, description } = texts;

                    if (finalTip) {
                        texts = stringsRes.tripHistory.tipsAndRating.tipThanks;
                        icon = IconAxel.HeartHands;
                        title = `${rps(texts.title, { amount: finalTip })}${stringsRes.booking.euroCurrency.trim()}`;
                        description = rps(texts.description, {
                            name: ride?.driver?.firstname ?? capitalizeFirstLetter(stringsRes.common.yourDriver),
                        });
                    }

                    ToastWithIcon('success', description, icon, title);
                },
            });
        }
    };

    //= ======================================//
    //= ============== Renders ===============//
    //= ======================================//

    const renderTipsButtons = (): JSX.Element[] => {
        const dataEntries: number[] = DEFAULT_TIPS_ARRAY;
        return dataEntries.map((el: number): JSX.Element => {
            return (
                <RadiusButton
                    id={`${el}`}
                    title={`${el}${stringsRes.booking.euroCurrency}`}
                    isSelected={tip === el}
                    onClick={onChangeTip(el)}
                    key={el}
                    className={classes.tipButton}
                    border
                />
            );
        });
    };

    return (
        <EditReservationWrapper
            {...props}
            onConfirm={onConfirm}
            onCancel={onConfirm}
            loadingConfirm={updateRide.isLoading}
            disableConfirm={rating === null || (showTips && !(customTip || tip))}
            labelConfirm={stringsRes.common.confirm}
            noCancel={!showTips}
            labelCancel={stringsRes.tripHistory.tipsAndRating.tipNo}
            buttonsClassName={clsx(classes.buttonsContainer, showTips && classes.buttonsContainerTips)}
            noSpacing
        >
            {!showTips ? (
                <>
                    <Text.Description14
                        text={rps(stringsRes.tripHistory.tipsAndRating.booking, {
                            date: formatDateFromTimestamp(
                                ride.start_date ?? ride.creation_date,
                                stringsRes.time.recurrenceFormat,
                            ),
                            destination: getStopFullname(findStop(ride?.stops, StopType.DROP_OFF)),
                        })}
                    />
                    <Space value={2} />
                    {/* <Text.Description16 text={stringsRes.tripHistory.tipsAndRating.rating} />
            <Space value={3} /> */}
                    <Rating
                        name="rating"
                        value={rating}
                        size="large"
                        className={classes.rating}
                        readOnly={!!ride.driver_rating?.score}
                        onChange={onChangeRating}
                        // max={5} // to change the max number of stars
                    />
                    {/* <Space value={4} />
                            <Text.Description16
                                customColor="grey"
                                text={stringsRes.tripHistory.tipsAndRating.comment}
                            /> */}

                    {showComment && (
                        <>
                            <Space value={2} />
                            <Separator height={1} />
                            <Space value={2} />
                            {/* TAGS */}
                            <div className={clsx(globalClasses.flexRow, classes.tagsTitle)}>
                                <Text.ShortRegularText14
                                    text={
                                        tags?.notes?.sort((n) => n.threshold)?.find((n) => rating <= n.threshold)
                                            ?.message
                                    }
                                />
                                <Text.ShortRegularText14 text={`${selectedTags?.length}/3`} />
                            </div>
                            <Space value={2} />
                            <div className={clsx(globalClasses.flexRow, classes.tagsContainer)}>
                                {tags?.tags?.map((tag) => (
                                    <RadiusButton
                                        id={tag}
                                        key={tag}
                                        title={tag}
                                        isSelected={selectedTags.includes(tag)}
                                        onClick={onChangeTag(tag)}
                                        disabled={
                                            !selectedTags.includes(tag) && selectedTags?.length === MAX_RATING_TAGS
                                        }
                                        mode="grey"
                                    />
                                ))}
                            </div>
                            <Space value={3} />

                            {/* COMMENT */}
                            <InputField
                                name="message"
                                label={`${stringsRes.tripHistory.tipsAndRating.commentPlaceholder} ${stringsRes.common.optional}`}
                                value={message}
                                // autoFocus
                                onChange={onMessageChange}
                                minRows={3}
                                maxRows={9}
                                InputProps={{ disableUnderline: true }}
                                multiline
                                className={classes.textArea}
                                onFocus={(e: any) => {
                                    const tmp = e.target.value;
                                    e.target.value = '';
                                    e.target.value = tmp;
                                }}
                            />
                        </>
                    )}
                </>
            ) : (
                <>
                    <Text.Description14
                        text={rps(stringsRes.tripHistory.tipsAndRating.tipTitle, {
                            driver: ride.driver?.firstname || stringsRes.common.yourDriver,
                        })}
                    />
                    <Space value={2} />

                    {isFetching && <Loader />}
                    {!isFetching && canAddTip && (
                        <>
                            <div className={classes.tipsContainer}>
                                <div className={classes.tipsBtnContainer}>{renderTipsButtons()}</div>
                                <InputField
                                    onlyNumber
                                    mode="price"
                                    name="customTip"
                                    value={customTip || ''}
                                    onChange={onChangeCustomTip}
                                    label={stringsRes.tripHistory.tipsAndRating.tipCustomShort}
                                    className={classes.tip}
                                />
                                <div className={clsx(globalClasses.flexRow, globalClasses.gap8)}>
                                    <SvgIcon
                                        icon={IconCommon.InformationOutline}
                                        size="mediumSmall"
                                        stroke="currentColor"
                                        fix={18}
                                    />
                                    <Text.Description14
                                        text={rps(stringsRes.tripHistory.tipsAndRating.tipMax, {
                                            amount: Math.min(ride.price_details.client_final_price, MAX_TIP_AMOUNT),
                                        })}
                                    />
                                </div>
                            </div>
                            <Space value={1} />
                        </>
                    )}
                </>
            )}

            <Space value={2} />
            <Separator height={1} />
            <Space value={2} />

            {/* <Space value={1} />
            <TextButton
                customColor="lightblue"
                title={stringsRes.tripHistory.reportIncident}
                onClick={onGoToSupport}
                className={classes.link}
            /> */}
        </EditReservationWrapper>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        textArea: {
            flex: 1,
            width: '100%',
        },
        link: {
            fontSize: 14,
        },
        rating: {
            fontSize: '1.67em',
            alignSelf: 'center',
            '& .MuiSvgIcon-root': {
                width: '1.5em',
                height: '1.5em',
            },
        },
        tagsTitle: {
            justifyContent: 'space-between',
            gap: 8,
        },
        tagsContainer: {
            gap: 12,
            flexWrap: 'wrap',
        },
        tip: {
            // width: '50%',
            // '@media (max-width: 600px)': {
            width: '100%',
            // },
        },
        tipsBtnContainer: {
            display: 'flex',
            flex: '100%',
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            overflowX: 'auto',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
        },
        tipButton: {
            minWidth: 78,
            padding: '12px 20px',
            borderRadius: 12,
            borderColor: 'transparent', // theme.palette.colors.background.menuSeparator,
            backgroundColor: `${theme.palette.colors.background.primaryElement}!important`,
            color: theme.palette.primary.main,
            fontSize: 16,
            fontWeight: 400,
            flex: 1,
            '&:hover': {
                borderColor: theme.palette.secondary.main,
                '@media (hover: none)': {
                    borderColor: 'transparent', // Disable hover on touch devices
                },
            },
            '&.selected': {
                backgroundColor: `${theme.palette.colors.background.primaryBtn}24 !important`,
            },
        },
        tipsContainer: {
            gap: 16,
            display: 'flex',
            flexDirection: 'column',
        },
        buttonsContainer: {
            '& > button': {
                width: '100%',
                maxWidth: 'unset !important',
            },
        },
        buttonsContainerTips: {
            gap: 8,
            flexDirection: 'column-reverse',
        },
    }),
);

export default EditHistoryTipsAndRating;
